import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, Validators, ReactiveFormsModule, FormControl, FormGroup, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FileUploaderComponent } from '../../../utility/file-uploader/file-uploader.component';
import { countryResponse } from '../../../models/country/countryResponse';
import { UtilityService } from '../../../services/utility/utility.service';
import { OlaPlacesResponse } from '../../../models/country/olaPlacesResponse';
import { AutoCompleteModule, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { InstituteService } from '../../../services/institute/institute.service';
import { SearchInstituteResponse } from '../../../models/institute/searchInstituteResponse';
import { InputNumberModule } from 'primeng/inputnumber';
import { MentorService } from '../../../services/account/mentor.service';
import { loginResponse } from '../../../models/account/loginResponse';
import { Observable } from 'rxjs';
import { apiErrorResponse } from '../../../models/error-models/apiErrorResponse';
import { MessageService } from 'primeng/api';
import { RegisterMentorRequest } from '../../../models/account/mentor/registerMentorRequest';

enum Step {
  step0, step1, step2, step3, step4, step5, step6, step7, step8, step9
}
interface place {
  name: string
}
@Component({
  selector: 'app-mentor-sign-up-steps',
  standalone: true,
  imports: [FormsModule, ButtonModule, InputTextModule, CheckboxModule, DropdownModule, RadioButtonModule, InputTextareaModule, FileUploaderComponent, AutoCompleteModule,
    ReactiveFormsModule, CommonModule, RouterModule, InputNumberModule],
  templateUrl: './mentor-sign-up-steps.component.html',
  styleUrl: './mentor-sign-up-steps.component.scss'
})

export class MentorSignUpStepsComponent implements OnInit {
  Step = Step;
  currentStep: Step = Step.step0;
  data: any;
  profileForm: any;
  loading: boolean = false;
  submitted: boolean = false;
  institutes: SearchInstituteResponse[] = [];
  file: File | Blob | undefined;
  token: string = '';
  email: string = '';

  dobDays: { name: string; value: string }[] = [];
  dobMonths: { name: string; value: string }[] = [];
  dobYears: { name: string; value: string }[] = [];
  
  showOtherServiceInput: boolean = false;
  otherServiceSpecControl = new FormControl('');
  
  showOtherSubjectInput: boolean = false;
  otherSubjectSpecControl = new FormControl('');
  
  checked = false;

  constructor(private router: Router, private formBuilder: FormBuilder,
    private utilityService: UtilityService, private instituteService: InstituteService,
    private mentorService: MentorService, private messageService: MessageService) {
    this.readRequestData();
  }

  ngOnInit(): void {
    this.genders = [
      { name: 'Male', value: 1 },
      { name: 'Female', value: 2 },
      { name: 'Other', value: 3 }
    ]
    this.loadCountries();
    this.setYearsOptions();
    this.setMonthsOptions();
    this.setDobDaysOptions();
    this.setDobMonthsOptions();
    this.setDobYearsOptions();
    this.buildForm();
  }

  atLeastOneServiceSpecializationValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const specialization = group.get('servicesSpecialization')?.value || [];
      const other = group.get('otherServiceSpecControl')?.value?.trim();

      if ((specialization && specialization.length > 0) || other) {
        return null;
      }

      return { servicesSpecializationRequired: true };
    };
  }

  atLeastOneSubjectSpecializationValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const specialization = group.get('subjectSpecialization')?.value || [];
      const other = group.get('otherSubjectSpecControl')?.value?.trim();

      if ((specialization && specialization.length > 0) || other) {
        return null;
      }

      return { subjectSpecializationRequired: true };
    };
  }

  serviceOptions = [
    { name: 'US Undergraduate', key: 'US Undergraduate' },
    { name: 'US Graduate', key: 'US Graduate' },

    { name: 'UK Undergraduate', key: 'UK Undergraduate' },
    { name: 'UK Graduate', key: 'UK Graduate' },
    { name: 'Canadian Undergraduate', key: 'Canadian Undergraduate' },
    { name: 'Canadian Graduate', key: 'Canadian Graduate' },

    { name: 'Indian Undergraduate', key: 'Indian Undergraduate' },
    { name: 'India Graduate', key: 'India Graduate' },
    { name: 'Engineering', key: 'Engineering' },
    { name: 'Law / UPSC', key: 'Law / UPSC' },
    { name: 'Medical for India', key: 'Medical for India' },
    { name: 'Medical for abroad', key: 'Medical for abroad' },
    { name: 'MBA', key: 'MBA' },
    { name: 'Undergraduate in other countries', key: 'Undergraduate in other countries' }
  ];

  subjectOptions = [
    { name: 'Business', key: 'Business' },
    { name: 'Transfer', key: 'Transfer' },
    { name: 'Athletics', key: 'Athletics' },
    { name: 'Performing Arts', key: 'Performing Arts' }
  ];

  onOtherServiceChange(event: CheckboxChangeEvent){
    if(event.checked.length !==0){
      this.showOtherServiceInput = true;
      this.otherServiceSpecControl.setValidators([Validators.required]);
    }else{
      this.showOtherServiceInput = false;
      this.otherServiceSpecControl.setValue('');
      this.otherServiceSpecControl.clearValidators();
    }
    this.otherServiceSpecControl.updateValueAndValidity();
  }

  onOtherSubjectChange(event: CheckboxChangeEvent){
    if(event.checked.length !==0){
      this.showOtherSubjectInput = true;
      this.otherSubjectSpecControl.setValidators([Validators.required]);
    }else{
      this.showOtherSubjectInput = false;
      this.otherSubjectSpecControl.setValue('');
      this.otherSubjectSpecControl.clearValidators();
    }
    this.otherSubjectSpecControl.updateValueAndValidity();
  }

  setDobDaysOptions() {
    this.dobDays = Array.from({ length: 30 }, (_, i) => ({
      name: (i + 1).toString().padStart(2, '0'), // Add leading zero for single-digit days
      value: (i + 1).toString().padStart(2, '0') // Use this as the actual value
    }));
  }

  setDobMonthsOptions() {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    this.dobMonths = monthNames.map((name, i) => ({
      name, // Use the month name directly
      value: (i + 1).toString().padStart(2, '0')
    }));
  }

  setDobYearsOptions() {
    const currentYear = new Date().getFullYear();
    this.dobYears = Array.from({ length: currentYear - 1960 + 1 }, (_, i) => ({
      name: (1960 + i).toString(), // Convert year to string for display
      value: (1960 + i).toString() // Use this as the actual value
    }));
  }

  readRequestData() {
    const navigation = this.router.getCurrentNavigation();
    if (navigation && navigation?.extras.state) {
      const { token, email } = navigation.extras.state;
      if (token && email) {
        this.token = token;
        this.email = email;
      }
      else {
        this.router.navigate(['/mentor-signup']);
      }
    }
    else {
      this.router.navigate(['/mentor-signup']);
    }
  }

  onFileSelection($event: import("ngx-awesome-uploader").FilePreviewModel[]) {
    if ($event.length > 0) {
      this.file = $event[0].file
    }
  }

  onFileRemoved($event: import("ngx-awesome-uploader").FilePreviewModel[]) {
    if ($event.length > 0) {
      this.file = undefined;
    }
  }

  onUnderGraduteCheckedChange($event: CheckboxChangeEvent) {
    var underGradulateControl = this.profileForm.get('educataion.undergraduateUniversity');
    if (underGradulateControl) {
      if ($event.checked) {
        underGradulateControl?.setValidators(Validators.required);
      }
      else {
        underGradulateControl.clearValidators();
      }
      underGradulateControl.updateValueAndValidity();
    }
  }


  onMastersCheckedChange($event: CheckboxChangeEvent) {
    var mastersUniversityControl = this.profileForm.get('educataion.mastersUniversity');
    if (mastersUniversityControl) {
      if ($event.checked) {
        mastersUniversityControl?.setValidators(Validators.required);
      }
      else {
        mastersUniversityControl.clearValidators();
      }
      mastersUniversityControl.updateValueAndValidity();
    }
  }

  filterInstitutes(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 3) {
      this.instituteService.searchInstitute(event.query).subscribe({
        next: (response: SearchInstituteResponse[]) => {
          if (response.length > 0) {
            this.institutes = response
          }
          else {
            this.institutes = [{ name: event.query }];
          }
        }
      });
    } else {
      this.institutes = [{ name: event.query }];
    }
  }

  genders: { name: string; value: number }[] = [];
  countries: countryResponse[] = [];
  places: place[] = [];
  years: { name: string; value: string }[] = [];
  months: { name: string; value: string }[] = [];

  private loadCountries(): void {
    this.utilityService.getCountries().subscribe({
      next: (response) => this.countries = response.result,
      error: (err) => console.warn("Error loading countries:", err),
    });
  }

  setMonthsOptions() {
    this.months = Array.from({ length: 13 }, (_, i) => ({
      name: i.toString().padStart(2, '0'), // Use '00' to '12' format
      value: i.toString() // Values from 0 to 12 (no padding)
    }));
  }

  setYearsOptions() {
    this.years = Array.from({ length: 40 }, (_, i) => ({
      name: i.toString(), // Convert year to string for display (1 to 40)
      value: i.toString() // Use this as the actual value (1 to 40)
    }));
  }


  filterPlaces(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 3) {
      this.utilityService.getOLAPlaces(event.query).subscribe({
        next: (response: OlaPlacesResponse) => this.places = response.predictions.map(item => ({ name: item.description })),
      });
    } else {
      this.places = [{ name: event.query }];
    }
  }

  buildForm() {
    // Create the form with the necessary fields
    this.profileForm = this.formBuilder.group({
      name: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: [''],
      }),
      educataion: this.formBuilder.group({
        chkGraduationUndergraduate: [false, Validators.required],
        undergraduateUniversity: [''],
        chkGraduationMasters: [false],
        mastersUniversity: [''],
      }),
      bornOn: this.formBuilder.group({
        dobDays: ['', Validators.required],
        dobMonths: ['', Validators.required],
        dobYears: ['', Validators.required]
      }),
      gender: ['', Validators.required],
      nationality: ['', Validators.required],
      companySchool: [''],
      designation: [''],
      experience: this.formBuilder.group({
        years: ['', Validators.required],
        months: ['', Validators.required],
      }),
      whydoYouWantToBeMentor: ['', Validators.required],
      servicesSpecialization: [''],
      otherServiceSpecControl: [''],
      subjectSpecialization: [''],
      otherSubjectSpecControl: [''],
      resume: [null, Validators.required],
    },
    {
      validators: [this.atLeastOneServiceSpecializationValidator(), this.atLeastOneSubjectSpecializationValidator()]
    });
    this.otherServiceSpecControl = this.profileForm.get('otherServiceSpecControl') as FormControl;
    this.otherSubjectSpecControl = this.profileForm.get('otherSubjectSpecControl') as FormControl;
  }

  continue() {
    this.submitted = true;
    // Validate current step
    if (!this.validateCurrentStep()) {
      return;
    }

    // If we're on step 10, handle form submission
    if (this.currentStep === Step.step9) {
      this.submitForm();
    } else {
      this.submitted = false;
      this.currentStep++;
    }
  }

  validateCurrentStep(): boolean {
    const formControls: Record<Step, string | null> = {
      [Step.step0]: 'name.firstName',
      [Step.step1]: 'educataion',
      [Step.step2]: 'bornOn',
      [Step.step3]: 'gender',
      [Step.step4]: 'nationality',
      [Step.step5]: 'companySchool',
      [Step.step6]: 'experience',
      [Step.step7]: 'howPlanOnUsingGradpilots',
      [Step.step8]: null, // Handled by group validators
      [Step.step9]: null
    };

    // Special case for step9 - Image validation
    if (this.currentStep === Step.step9) {
      if (!this.file) {
        return false;
      }
      return true;
    }

    //education
    if (this.currentStep == Step.step1) {
      const control = this.profileForm.get('educataion.chkGraduationUndergraduate');
      if (control && control.value == false) {
        return false;
      }
    }

    // Get the control path for the current step
    const stepControl = formControls[this.currentStep];

    // Check step 8 specifically because the validators are on the group
    if (this.currentStep === Step.step8) {
      if (this.profileForm.errors?.servicesSpecializationRequired || this.profileForm.errors?.subjectSpecializationRequired) {
        return false;
      }
    }

    // Validate the form control if it's not null
    if (stepControl) {
      const control = this.profileForm.get(stepControl);
      if (control && control.invalid) {
        return false;
      }
    }

    return true;
  }

  submitForm(): void {
    this.loading = true;
    const formData = this.profileForm.value;
    const totalExperience = this.calculateTotalExperience(formData.experience);
    const bornOn = formData.bornOn.dobDays.value + "/" + formData.bornOn.dobMonths.value + "/" + formData.bornOn.dobYears.value;
    
    let finalServiceSpec: string[] = formData.servicesSpecialization || [];
    if(this.showOtherServiceInput && this.otherServiceSpecControl.value){
      finalServiceSpec.push(this.otherServiceSpecControl.value)
    }

    let finalSubjectSpec: string[] = formData.subjectSpecialization || [];
    if(this.showOtherSubjectInput && this.otherSubjectSpecControl.value){
      finalSubjectSpec.push(this.otherSubjectSpecControl.value)
    }

    this.convertBlobtoBase64(this.file).then((result) => {
      const mentorData: RegisterMentorRequest = {
        firstName: formData.name.firstName,
        lastName: formData.name.lastName,
        isGraduate: formData.educataion.chkGraduationUndergraduate,
        graduationCollageName: formData.educataion.undergraduateUniversity,
        isMasters: formData.educataion.chkGraduationMasters,
        mastersCollageName: formData.educataion.mastersUniversity,
        bornOn: bornOn,
        gender: formData.gender.value,
        nationality: formData.nationality,
        companyOrSchool: formData.companySchool,
        designation: formData.designation,
        totalExperience: totalExperience,
        whyToBeMentor: formData.whydoYouWantToBeMentor,
        servicesSpecialization: finalServiceSpec,
        subjectSpecialization: finalSubjectSpec,
        resume: result,
        email: this.email,
        temporaryToken: this.token
      };
      this.mentorService.registerUser(mentorData).subscribe({
        next: (response: loginResponse) => {
          this.currentStep++;
          this.loading = false;
        },
        error: (err: apiErrorResponse) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message });
          this.loading = false;
        }
      });
    }).catch((error) => {
      this.loading = false;
    });
  }

  onFileHandlingError(message: string) {
    if (message === 'FILE_MAX_SIZE')
      this.messageService.add({ severity: 'error', summary: 'FILE_MAX_SIZE', detail: `File size should be less than 20 MB` });
    else {
      this.messageService.add({ severity: 'error', summary: 'File Error', detail: message });
    }
  }

  calculateTotalExperience(experience: any): string {
    // Ensure that years and months are valid numbers, defaulting to 0 if they are not
    const years = Number(experience.years.value) || 0; // Convert to number, default to 0 if invalid
    const months = Number(experience.months.value) || 0; // Convert to number, default to 0 if invalid
    return (years + (months / 12)).toFixed(2);
  }

  back() {
    if (this.currentStep > 0) {
      this.currentStep = this.currentStep - 1;
    }
  }

  convertBlobtoBase64(file: File | Blob | undefined): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject('No file or blob provided.');
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  }
}
